import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Filler,
    Tooltip,
    Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { MwSpinnerButton } from '../ui';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useMemo } from 'react';

ChartJS.register(
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Filler,
    Tooltip,
    Legend
);

const debounce = (func, timeout = 300) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func.apply(this, args), timeout);
    };
};

const LineChart = ({ datasetData = [], datasetLabel, loading = false, className = '' }) => {
    const [t] = useTranslation('global');
    const chartRef = useRef(null);
    const containerRef = useRef(null);

    const palette = useMemo(() => {
        const toRgba = (value, alpha) => {
            if (!value) return `rgba(7, 11, 15, ${alpha})`;
            if (value.startsWith('#')) {
                const hex = value.slice(1);
                const full = hex.length === 3
                    ? hex.split('').map((c) => c + c).join('')
                    : hex;
                const intValue = parseInt(full, 16);
                if (!Number.isNaN(intValue)) {
                    const r = (intValue >> 16) & 255;
                    const g = (intValue >> 8) & 255;
                    const b = intValue & 255;
                    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                }
            }
            if (value.startsWith('rgba(')) {
                return value.replace(/rgba\(([^,]+),([^,]+),([^,]+),[^)]+\)/, `rgba($1,$2,$3,${alpha})`);
            }
            if (value.startsWith('rgb(')) {
                return value.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
            }
            return `rgba(7, 11, 15, ${alpha})`;
        };

        if (typeof window === 'undefined') {
            return { primary: '#070b0f', fill: 'rgba(7, 11, 15, 0.18)' };
        }
        const computed = getComputedStyle(document.documentElement);
        const primary = computed.getPropertyValue('--brandColor')?.trim() || '#070b0f';
        const fill = toRgba(primary, 0.18);
        return { primary, fill };
    }, []);

    const data = useMemo(() => ({
        labels: [ 
            t("DashBoard.months.january"),
            t("DashBoard.months.february"),
            t("DashBoard.months.march"),
            t("DashBoard.months.april"),
            t("DashBoard.months.may"),
            t("DashBoard.months.june"),
            t("DashBoard.months.july"),
            t("DashBoard.months.august"),
            t("DashBoard.months.september"),
            t("DashBoard.months.october"),
            t("DashBoard.months.november"),
            t("DashBoard.months.december")
        ],
        datasets: [
            {
                label: datasetLabel || t("DashBoard.currentYearSales"),
                data: datasetData,
                borderColor: palette.primary,
                backgroundColor: palette.fill,
                pointBackgroundColor: palette.primary,
                pointBorderColor: '#FFFFFF',
                pointBorderWidth: 2,
                borderWidth: 2,
                fill: true,
                tension: 0.4,
            }
        ]
    }), [t, datasetData, datasetLabel, palette]); 

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        devicePixelRatio: 1.5,
        animation: {
            duration: 150,
            easing: 'easeOutCubic'
        },
        plugins: {
            legend: {
                labels: {
                    font: {
                        size: 14,
                        family: '"Cairo", sans-serif',
                    },
                    color: palette.primary,
                }
            },
            tooltip: {
                enabled: true,
                titleFont: {
                    family: '"Cairo", sans-serif',
                },
                bodyFont: {
                    family: '"Cairo", sans-serif',
                },
                titleColor: palette.primary,
                bodyColor: palette.primary,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderColor: 'rgba(203, 213, 225, 0.8)',
            }
        },
        scales: {
            x: {
                ticks: {
                    color: palette.primary,
                    font: {
                        family: '"Cairo", sans-serif',
                    }
                },
                grid: {
                    color: 'rgba(203, 213, 225, 0.5)',
                }
            },
            y: {
                ticks: {
                    color: palette.primary,
                    font: {
                        family: '"Cairo", sans-serif',
                    }
                },
                grid: {
                    color: 'rgba(203, 213, 225, 0.5)',
                }
            }
        }
    }), [palette]); 
    
    useEffect(() => {
        if (!containerRef.current) return;

        const resizeHandler = debounce(() => {
            if (chartRef.current?.chartInstance) {
                chartRef.current.chartInstance.resize(250);
            }
        }, 300);

        const observer = new ResizeObserver(resizeHandler);
        observer.observe(containerRef.current);
        
        return () => observer.disconnect();
    }, []);

    return (
        <div className="chart-wrapper w-full transition-all duration-150">
            {loading ? (
                <div className="rounded-2xl bg-white flex-1 border-2 brand-border flex items-center justify-center md:px-12 h-96">
                    <MwSpinnerButton withLabel={false} isFullCenter={true} />
                </div>
            ) : (
                <div 
                    ref={containerRef}
                    className={`rounded-2xl bg-white flex-1 border-2 brand-border h-96 ${className}`.trim()}
                    style={{ 
                        overflow: 'hidden',
                        position: 'relative',
                        width: '100%',
                        transition: 'margin-left 0.3s ease'
                    }}
                >
                    <div style={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        padding: '8px',
                        transition: 'all 0.3s ease'
                    }}>
                        <Line 
                            data={data} 
                            options={options}
                            ref={chartRef}
                            className="transition-all duration-150"
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default LineChart
